/**
 * Design System Tokens
 * 
 * Centralized design tokens for the MO Insights application.
 * Based on the organization management UI design.
 */

// Color Palette - Cyan/Turquoise accent theme
export const colors = {
  // Primary - Cyan accent
  primary: {
    50: '#E5F8FB',
    100: '#B8EDF5',
    200: '#8AE2EF',
    300: '#5CD7E9',
    400: '#2ECCE3',
    500: '#02B3E3', // Main primary
    600: '#009FCC',
    700: '#008BB5',
    800: '#00789E',
    900: '#005A77',
  },
  
  // Neutral - Gray scale
  neutral: {
    0: '#FFFFFF',
    50: '#F8F9FA',
    100: '#F4F6F8', // Background
    150: '#EBEEF1',
    200: '#E0E3E6',
    300: '#CED4DA',
    400: '#ADB5BD',
    500: '#6C757D',
    600: '#54565A', // Text secondary
    700: '#495057',
    800: '#343A40',
    900: '#212529', // Text primary
  },
  
  // Semantic colors
  success: {
    light: '#D1FAE5',
    main: '#10B981',
    dark: '#047857',
  },
  
  warning: {
    light: '#FEF3C7',
    main: '#F59E0B',
    dark: '#D97706',
  },
  
  error: {
    light: '#FEE2E2',
    main: '#EF4444',
    dark: '#DC2626',
  },
  
  info: {
    light: '#DBEAFE',
    main: '#3B82F6',
    dark: '#2563EB',
  },
} as const;

// Spacing scale (in pixels, for use with MUI's sx prop)
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
} as const;

// Border radius
export const radii = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

// Typography
export const typography = {
  fontFamily: {
    display: '"Chakra Petch", sans-serif',
    body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", "Fira Code", monospace',
  },
  
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.8125rem',  // 13px
    base: '0.875rem', // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
  },
  
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

// Shadows
export const shadows = {
  none: 'none',
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  card: '0 2px 8px rgba(0, 0, 0, 0.06)',
  elevated: '0 4px 12px rgba(0, 0, 0, 0.08)',
} as const;

// Transitions
export const transitions = {
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
} as const;

// Z-index layers
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  overlay: 300,
  modal: 400,
  tooltip: 500,
} as const;

// Component-specific tokens
export const components = {
  sidebar: {
    width: 280,
    collapsedWidth: 80,
    background: colors.neutral[0],
    borderColor: colors.neutral[200],
  },
  
  card: {
    background: colors.neutral[0],
    borderColor: colors.neutral[200],
    borderRadius: radii.lg,
    padding: spacing.lg,
  },
  
  input: {
    height: 40,
    borderColor: colors.neutral[300],
    focusBorderColor: colors.primary[500],
    background: colors.neutral[0],
    borderRadius: radii.md,
  },
  
  button: {
    height: {
      sm: 32,
      md: 40,
      lg: 48,
    },
    borderRadius: radii.md,
  },
  
  chip: {
    height: 32,
    borderRadius: radii.md,
  },
  
  navItem: {
    height: 44,
    activeBackground: `${colors.primary[500]}14`, // 8% opacity
    activeBorderColor: colors.primary[500],
    hoverBackground: colors.neutral[100],
  },
} as const;

// Export type helpers
export type ColorShade = keyof typeof colors.primary;
export type SpacingKey = keyof typeof spacing;
export type RadiiKey = keyof typeof radii;

